import {
  atom,
  DefaultValue,
  selectorFamily,
  useRecoilValue,
  waitForAll,
} from 'recoil';
import { fetchLoanById } from 'app/pages/ManageLoan/ManageLoanHandle';
import { Vault } from 'types';
import { triggerRefresh } from 'store/loanStore';
import { polkaService, serviceData } from 'store/app';
import {
  balanceSelector,
  getType,
  initDataSelector,
  pricesSelector,
} from 'app/pages/LoanList/store';
import valueToText from 'app/components/context/ValueToText';
import { amountFromWei } from 'utils/contract/common-function';
import { BigNumber } from 'bignumber.js';
import { hundred, suffixInterest, zero } from 'constants/number';
import { one } from 'constants/zero';
import { HOUR, SECONDS_PER_YEAR } from 'constants/variables';
import { parseLoanModel } from 'utils/common-function';
import { LoanData } from 'app/pages/NewLoan/store/loanStore';

export const existLoanInfo = selectorFamily<Vault | undefined, string>({
  key: 'existLoanInfo',
  get:
    loanId =>
    async ({ get }) => {
      get(triggerRefresh);
      return await fetchLoanById(loanId);
    },
});

export const manageLoanStore = atom<any>({
  key: 'manageLoanStore',
  default: undefined,
});

export const manageLoanCondition = atom<any>({
  key: 'manageLoanCondition',
  default: undefined,
});

interface ILoanDetailCalcProp {
  tokenId: string;
  liquidationRatio: BigNumber;
  maximumTotalDebitValue: BigNumber;
  liquidationPenalty: BigNumber;
  debtFloor: BigNumber;
  debt: BigNumber;
  collateral: BigNumber;
  currentPrice: BigNumber;
  nextPrice: BigNumber;
  ilkDebtAvailable: BigNumber;
  collateralizationDangerThreshold: BigNumber;
  collateralizationWarningThreshold: BigNumber;
  stabilityFee: BigNumber;
  zUSDBalance: BigNumber;
  debitExchangeRate: BigNumber;
  constDebt: BigNumber;
  requiredCollateralRatio: BigNumber;
}

export const zUSDTokenId = '1';

const fetchLoanDetailCalc = (state: ILoanDetailCalcProp) => {
  const {
    requiredCollateralRatio,
    liquidationRatio,
    maximumTotalDebitValue,
    liquidationPenalty,
    debtFloor,
    debt,
    collateral,
    currentPrice,
    nextPrice,
    ilkDebtAvailable,
    collateralizationDangerThreshold,
    collateralizationWarningThreshold,
    stabilityFee,
    debitExchangeRate,
  } = state;
  const valueDeposited = collateral.times(currentPrice);
  // const liquidationPrice = debt.times(liquidationRatio).div(freeCollateral);
  // const collateralizationRatio = valueDeposited.div(debt);
  // const daiYieldFromLockedCollateral = freeCollateral.times(currentPrice).div(liquidationRatio)
  console.log('stabilityFee', stabilityFee);
  const debtOffset = debitExchangeRate;

  // const debtOffset2 = !debt.isZero()
  //   ? debt
  //       .times(one.plus(stabilityFee.div(SECONDS_PER_YEAR)).pow(HOUR * 5))
  //       .minus(debt)
  //       .dp(18, BigNumber.ROUND_DOWN)
  //   : new BigNumber('1e-18');
  console.log('collateralizationDangerThreshold', {
    collateralizationDangerThreshold:
      collateralizationDangerThreshold.toString(),
    debt: debt.toString(),
    currentPrice: currentPrice.toString(),
    collateral: collateral.toString(),
  });
  const backingCollateral = debt
    .times(collateralizationDangerThreshold)
    .div(currentPrice);

  console.log('backing', backingCollateral.toString());

  const backingCollateralAtNextPrice = debt
    .times(liquidationRatio)
    .div(nextPrice);

  const backingCollateralUSD = backingCollateral.times(currentPrice);
  const backingCollateralUSDAtNextPrice =
    backingCollateralAtNextPrice.times(nextPrice);

  const freeCollateral = backingCollateral.gte(collateral)
    ? zero
    : collateral.minus(backingCollateral);
  const freeCollateralAtNextPrice = backingCollateralAtNextPrice.gte(collateral)
    ? zero
    : collateral.minus(backingCollateralAtNextPrice);

  const freeCollateralUSD = freeCollateral.times(currentPrice);
  const freeCollateralUSDAtNextPrice =
    freeCollateralAtNextPrice.times(nextPrice);

  const collateralUSD = collateral.times(currentPrice);
  const collateralUSDAtNextPrice = nextPrice
    ? collateral.times(nextPrice)
    : currentPrice;

  const collateralizationRatio = debt.isZero() ? zero : collateralUSD.div(debt);
  const collateralizationRatioAtNextPrice = debt.isZero()
    ? zero
    : collateralUSDAtNextPrice.div(debt);
  console.log('requiredCollateralRatio', requiredCollateralRatio.toString());
  // const maxAvailableDebt = collateralUSD.div(liquidationRatio);
  const maxAvailableDebt = collateralUSD.div(requiredCollateralRatio);
  // const maxAvailableDebtAtNextPrice =
  //   collateralUSDAtNextPrice.div(liquidationRatio);
  const maxAvailableDebtAtNextPrice = collateralUSDAtNextPrice.div(
    requiredCollateralRatio,
  );

  console.log('maxAvailableDebt', {
    requiredCollateralRatio: requiredCollateralRatio.toString(),
    debt: debt.toString(),
  });

  const availableDebt = debt.lt(collateralUSD.div(requiredCollateralRatio))
    ? maxAvailableDebt.minus(debt)
    : zero;

  const availableDebtAtNextPrice = debt.lt(maxAvailableDebtAtNextPrice)
    ? maxAvailableDebtAtNextPrice.minus(debt)
    : zero;

  const liquidationPrice = collateral.eq(zero)
    ? zero
    : debt.times(liquidationRatio).div(collateral);

  console.log(availableDebt.toString());
  console.log(ilkDebtAvailable.toString());

  const daiYieldFromLockedCollateral = availableDebt.lt(ilkDebtAvailable)
    ? availableDebt
    : ilkDebtAvailable.gt(zero)
    ? ilkDebtAvailable
    : zero;
  const atRiskLevelWarning =
    collateralizationRatio.gte(collateralizationDangerThreshold) &&
    collateralizationRatio.lt(collateralizationWarningThreshold);

  const atRiskLevelDanger =
    collateralizationRatio.gte(liquidationRatio) &&
    collateralizationRatio.lt(collateralizationDangerThreshold);

  const underCollateralized =
    !collateralizationRatio.isZero() &&
    collateralizationRatio.lt(liquidationRatio);

  const atRiskLevelWarningAtNextPrice =
    collateralizationRatioAtNextPrice.gte(collateralizationDangerThreshold) &&
    collateralizationRatioAtNextPrice.lt(collateralizationWarningThreshold);

  const atRiskLevelDangerAtNextPrice =
    collateralizationRatioAtNextPrice.gte(liquidationRatio) &&
    collateralizationRatioAtNextPrice.lt(collateralizationDangerThreshold);

  const underCollateralizedAtNextPrice =
    !collateralizationRatioAtNextPrice.isZero() &&
    collateralizationRatioAtNextPrice.lt(liquidationRatio);

  return {
    ...state,
    availableDebt,
    maxAvailableDebt,
    maxAvailableDebtAtNextPrice,
    liquidationPrice,
    collateralizationRatio,
    daiYieldFromLockedCollateral,
    backingCollateral,
    backingCollateralAtNextPrice,
    backingCollateralUSD,
    backingCollateralUSDAtNextPrice,
    freeCollateral,
    freeCollateralAtNextPrice,
    freeCollateralUSD,
    freeCollateralUSDAtNextPrice,
    availableDebtAtNextPrice,
    atRiskLevelWarning,
    atRiskLevelDanger,
    underCollateralized,
    atRiskLevelWarningAtNextPrice,
    atRiskLevelDangerAtNextPrice,
    underCollateralizedAtNextPrice,
    debtOffset,
    lockedCollateral: collateral,
    lockedCollateralUSD: collateral.times(currentPrice),
  };
};

export const loanDetailSelector = selectorFamily<any, any>({
  key: 'loanDetailSelector',
  get:
    ({ tokenId, owner }) =>
    async ({ get }) => {
      // get(refreshManageLoanSelector);
      // try {
      //   const { api } = get(polkaService) ?? {};
      const [t, initData, price, zUSDBalance] = get(
        waitForAll([
          polkaService,
          serviceData,
          pricesSelector(tokenId),
          balanceSelector(zUSDTokenId),
        ]),
      ) as any;
      const { api } = t;
      const { globalInterest } = initData;
      // const initData = get(serviceData);
      // if (!api || !initData) {
      //   console.log('11111112222222')
      //   return;
      // }
      const asset =
        initData.assetArray[
          initData.assetArray.findIndex(e => e.tokenId === tokenId)
        ];
      const loanAssets = await api.query.loans.positions(tokenId, owner);
      const type = getType(api, 'loans', 'positions');
      const result = valueToText(type, loanAssets);

      const debitExchangeRateQuery =
        await api.query.cdpEngine.debitExchangeRate(tokenId);
      const debitExchangeRateType = getType(
        api,
        'cdpEngine',
        'debitExchangeRate',
      );
      const debitExchangeRate = valueToText(
        debitExchangeRateType,
        debitExchangeRateQuery,
      );

      // const price = get(pricesSelector(tokenId));
      // const [] = get(waitForAll([pricesSelector(tokenId), balanceSelector(zUSDTokenId)]))
      console.log('zUSDBalance', zUSDBalance.free?.toString());
      console.log('exchangeRate', debitExchangeRate);
      console.log('ex', result.debit);
      const convertLoan: ILoanDetailCalcProp = {
        ...asset,
        liquidationRatio: amountFromWei(
          new BigNumber(asset.liquidationRatio),
          18,
        ),
        maximumTotalDebitValue: amountFromWei(
          new BigNumber(asset.maximumTotalDebitValue),
          18,
        ),
        liquidationPenalty: asset.liquidationPenalty
          ? amountFromWei(new BigNumber(asset.liquidationPenalty), 18)
          : zero,
        debtFloor: hundred,
        constDebt: amountFromWei(new BigNumber(result.debit), 18),
        debt:
          result.debit === '0'
            ? zero
            : amountFromWei(
                new BigNumber(result.debit).multipliedBy(
                  amountFromWei(new BigNumber(debitExchangeRate), 18),
                ),
                18,
              ),
        collateral: amountFromWei(new BigNumber(result.collateral), 18),

        currentPrice: amountFromWei(new BigNumber(price), 18),
        nextPrice: amountFromWei(new BigNumber(price), 18),
        zUSDBalance: zUSDBalance.free,
        requiredCollateralRatio: amountFromWei(
          new BigNumber(asset.requiredCollateralRatio),
          18,
        ),
        collateralizationDangerThreshold: asset.requiredCollateralRatio
          ? amountFromWei(new BigNumber(asset.requiredCollateralRatio), 18)
          : zero,
        collateralizationWarningThreshold: asset.notifiedCollateralRatio
          ? amountFromWei(new BigNumber(asset.notifiedCollateralRatio), 18)
          : zero,
        ilkDebtAvailable: amountFromWei(
          new BigNumber(asset.maximumTotalDebitValue),
          18,
        ),
        // stabilityFee: amountFromWei(
        //   new BigNumber(asset.interestRatePerSec ?? zero),
        // )
        //   .multipliedBy(suffixInterest)
        //   .plus(globalInterest ?? zero),
        stabilityFee: zero,
        debitExchangeRate:
          debitExchangeRate === '<none>'
            ? initData.defaultExchangeRate
              ? amountFromWei(new BigNumber(initData.defaultExchangeRate), 18)
              : zero
            : amountFromWei(new BigNumber(debitExchangeRate), 18),
      };

      console.log('convertLoan', convertLoan);
      const loanCalc = fetchLoanDetailCalc(convertLoan);
      console.log('loanCalc', loanCalc);
      return loanCalc;
      // } catch (err) {
      //   console.log('7777777')
      //
      //   console.log('err.message', await err);
      // }
    },
});

export const refreshManageLoanSelector = atom<number>({
  key: 'refreshManageLoanSelector',
  default: 0,
});

export const manageLoanSelector = selectorFamily<
  LoanData,
  { tokenId: string; owner: string }
>({
  key: 'manageLoanSelector',
  get:
    ({ tokenId, owner }) =>
    async ({ get }) => {
      // try {
      console.log('rereun');
      // get(refreshManageLoanSelector);
      // let [loanInfo, loanDetail] = await Promise.all([
      //   await get(initDataSelector(tokenId)),
      //   await get(loanDetailSelector({ tokenId: tokenId, owner })),
      // ]);

      const [loanInfo, loanDetail] = get(
        waitForAll([
          initDataSelector(tokenId),
          loanDetailSelector({ tokenId: tokenId, owner }),
        ]),
      );

      // console.log('test', t)

      // do {
      //   loanInfo = await get(initDataSelector(tokenId))
      // } while (typeof loanInfo === 'undefined')

      // while (!loanDetail) {
      //   loanDetail = await get(loanDetailSelector({ tokenId: tokenId, owner }))
      // }

      console.log('[loanInfo, loanDetail]', [loanInfo, loanDetail]);

      return {
        ...parseLoanModel(loanInfo),
        detailData: loanDetail,
      } as LoanData;
      // } catch (err) {
      //   // const [loanInfo, loanDetail] = await err
      //   console.log('zzz', err)
      //
      //   // return {
      //   //   ...parseLoanModel(loanInfo),
      //   //   detailData: loanDetail,
      //   // } as LoanData;
      // }
    },
});
